import { Classes, Dialog, InputGroup, TextArea } from "@blueprintjs/core";
import { Form, Formik, FormikHelpers } from "formik";
import { DatePicker } from "modules/common/components/form/DatePicker";
import { StandardFormButtons } from "modules/common/components/form/StandardFormButtons";
import { StandardFormInput } from "modules/common/components/form/StandardFormInput";
import { useTextAreaAutoHeight } from "modules/common/services/form/textarea";
import moment from "moment";
import React, { useCallback, useMemo } from "react";
import * as yup from "yup";
import {
    Milestone,
    MILESTONE_DAY_FORMAT,
    MILESTONE_NAME_MAX_LENGTH,
    MilestoneDraft,
} from "../models/milestones-store";
import styles from "./MilestoneDialog.module.scss";

interface FormValues {
    name: string;
    day: Date | null;
    comment: string;
}

const schema = yup.object().shape({
    name: yup.string().label("Название").required().max(MILESTONE_NAME_MAX_LENGTH),
    day: yup.date().label("Дата").nullable().required(),
    comment: yup.string().label("Комментарий").notRequired(),
});

interface MilestoneDialogProps {
    /** Веха для правки или черновик новой (id пустой) */
    milestone: MilestoneDraft;
    /** Подпись проекта в заголовке: «0500 Название» */
    project: string;
    /** Нет права править — форма только для чтения, без кнопок */
    readOnly: boolean;
    onSave: (draft: MilestoneDraft) => Promise<boolean>;
    onRemove: (milestone: Milestone) => Promise<boolean>;
    onClose: () => void;
}

/**
 * Форма вехи: название, дата, комментарий. Одна на плановый график и на блок «Вехи»
 * в карточке проекта. Кто не может править вехи проекта, видит её только для чтения.
 */
export const MilestoneDialog: React.FC<MilestoneDialogProps> = (props) => {
    const { milestone, project, readOnly, onSave, onRemove, onClose } = props;

    const textAreaRef = useTextAreaAutoHeight();
    const isNew = !milestone.id;

    const initialValues = useMemo(
        (): FormValues => ({
            name: milestone.name,
            day: milestone.day ? moment(milestone.day, MILESTONE_DAY_FORMAT).toDate() : null,
            comment: milestone.comment,
        }),
        [milestone]
    );

    const submit = useCallback(
        async (values: FormValues, helpers: FormikHelpers<FormValues>) => {
            const saved = await onSave({
                ...milestone,
                name: values.name.trim(),
                day: moment(values.day!).format(MILESTONE_DAY_FORMAT),
                comment: (values.comment || "").trim(),
            });

            helpers.setSubmitting(false);
            saved && onClose();
        },
        [milestone, onSave, onClose]
    );

    const title = readOnly ? "Веха" : isNew ? "Новая веха" : "Изменить веху";

    return (
        <Dialog
            isOpen={true}
            onClose={onClose}
            title={title}
            isCloseButtonShown={true}
            backdropClassName="standard"
            canOutsideClickClose={readOnly}
            enforceFocus={false}
            className={`figma-dialog ${styles.dialog}`}
        >
            <Formik<FormValues> initialValues={initialValues} validationSchema={schema} onSubmit={submit}>
                {(formProps) => (
                    <Form autoComplete="off">
                        <div className={Classes.DIALOG_BODY}>
                            <div className={styles.project}>{project}</div>

                            <StandardFormInput name="name" schema={schema} small={true} className="planr-form-input">
                                {({ field }) => (
                                    <InputGroup
                                        id={field.name}
                                        className="planr-default-input"
                                        {...field}
                                        maxLength={MILESTONE_NAME_MAX_LENGTH}
                                        autoComplete="off"
                                        data-lpignore="true"
                                        autoFocus={!readOnly}
                                        disabled={readOnly}
                                    />
                                )}
                            </StandardFormInput>

                            <StandardFormInput name="day" schema={schema} small={true} className="planr-form-input">
                                {({ field, form }) => (
                                    <DatePicker
                                        value={field.value}
                                        readOnly={readOnly}
                                        onChange={(value) => {
                                            form.setFieldValue(field.name, value);
                                            form.setFieldTouched(field.name, true);
                                        }}
                                    />
                                )}
                            </StandardFormInput>

                            <StandardFormInput name="comment" schema={schema} small={true} className="planr-form-input">
                                {({ field }) => (
                                    <div className={`${Classes.INPUT_GROUP} planr-default-input`}>
                                        <TextArea
                                            id={field.name}
                                            {...field}
                                            autoResize={true}
                                            className={Classes.FILL}
                                            inputRef={textAreaRef}
                                            disabled={readOnly}
                                        />
                                    </div>
                                )}
                            </StandardFormInput>
                        </div>

                        {!readOnly && (
                            <div className={Classes.DIALOG_FOOTER}>
                                <div className={Classes.DIALOG_FOOTER_ACTIONS}>
                                    <StandardFormButtons
                                        {...formProps}
                                        isRemoved={false}
                                        isNewlyCreated={isNew}
                                        what={`веху «${milestone.name}»`}
                                        onRemove={async () => {
                                            formProps.setSubmitting(true);
                                            const removed = await onRemove(milestone);
                                            formProps.setSubmitting(false);
                                            removed && onClose();
                                        }}
                                    />
                                </div>
                            </div>
                        )}
                    </Form>
                )}
            </Formik>
        </Dialog>
    );
};
